// src/components/S44DeskPanel.js
//
// Record 593 (ruling 205 audit). RULING 205: a member's §44 notice is SURFACED, "on the claim and on the desk", and the
// officer acknowledges he has read it before he works the claim. This is the desk half: the filed claims on this desk
// whose claimant has a §44 notice in force that THIS officer has not acknowledged.
//
// HOW, AND WHY THIS SHAPE. A claim document carries no §44 field - the notice lives in claims/{id}/selfRepresentation,
// written by the claimant, whose update arms on the claim admit no such field. So this reads, per filed claim already
// on the desk, the same subcollection the claim page reads (firestore.rules: officers in scope read it) and, for a
// claim with a notice in force, the one acknowledgment document that would answer it ({uid}~{noticeDocId}). No query
// the rules refuse; one listener per filed claim, capped at MAX and said so when the cap bites. A read that fails is
// listed as unchecked, never as "nothing served".
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { noticeStateOf } from '../data/selfRepresentation';
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

const MAX = 150;
const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function S44DeskPanel({ claims, user, onOpen }) {
  const officer = !!user && (user.role === 'local_chair' || user.role === 'general_chair');
  const uid = (user && user.uid) || '';
  const filed = useMemo(() => (Array.isArray(claims) ? claims : [])
    .filter(c => c && c.status !== 'draft' && !c.returnOnly && (c.docId || c.id)), [claims]);
  const shown = filed.slice(0, MAX);
  const byId = useMemo(() => {
    const m = {};
    filed.forEach(c => { m[c.docId || c.id] = c; });
    return m;
  }, [filed]);
  const idsKey = shown.map(c => c.docId || c.id).join('\u0001');

  const [acts, setActs] = useState({});    // claim id -> the served act in force (with docId), or null
  const [acks, setAcks] = useState({});    // claim id -> true once THIS officer acknowledged that act
  const [errs, setErrs] = useState({});    // claim id -> why it could not be checked

  useEffect(() => {
    setActs({}); setErrs({});
    const ids = idsKey ? idsKey.split('\u0001') : [];
    if (!officer || !ids.length) return undefined;
    const unsubs = ids.map(id => onSnapshot(collection(db, 'claims', id, 'selfRepresentation'), (snap) => {
      const list = snap.docs.map(d => {
        const a = d.data();
        return { ...a, docId: d.id, servedAt: a.servedAt && a.servedAt.toDate ? a.servedAt.toDate().toISOString() : String(a.servedAt || '') };
      });
      const st = noticeStateOf(list);
      setActs(p => ({ ...p, [id]: st.notice ? st.act : null }));
    }, (e) => setErrs(p => ({ ...p, [id]: (e && e.code) || 'unreadable' }))));
    return () => unsubs.forEach(u => u());
  }, [officer, idsKey]);

  const servedKey = Object.keys(acts).filter(id => acts[id] && acts[id].docId).sort()
    .map(id => id + '\u0002' + acts[id].docId).join('\u0001');

  useEffect(() => {
    setAcks({});
    const pairs = servedKey ? servedKey.split('\u0001').map(x => x.split('\u0002')) : [];
    if (!officer || !uid || !pairs.length) return undefined;
    const unsubs = pairs.map(([id, actId]) => onSnapshot(doc(db, 'claims', id, 'selfRepresentationAcks', uid + '~' + actId),
      (snap) => setAcks(p => ({ ...p, [id]: snap.exists() })),
      (e) => setErrs(p => ({ ...p, [id]: (e && e.code) || 'unreadable' }))));
    return () => unsubs.forEach(u => u());
  }, [officer, uid, servedKey]);

  if (!officer) return null;
  const pending = Object.keys(acts).filter(id => acts[id] && acks[id] !== true && byId[id]);
  const unchecked = Object.keys(errs);
  const colour = pending.length ? C.accent : C.ok;

  return (
    <div style={{ border: '1px solid ' + (pending.length ? C.accent : C.line), background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '10px 14px', borderBottom: '1px solid ' + C.line, gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>§44 notice served — not acknowledged</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
            The member has served written notice that he represents himself. You are not locked out and the time
            limits have not stopped; read the notice on the claim and record that you have, before you work it.
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: colour, whiteSpace: 'nowrap' }}>{pending.length + ' claim(s)'}</div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim, lineHeight: 1.55 }}>
        {pending.length === 0
          ? 'No served notice on this desk is waiting for your acknowledgment.'
          : pending.map(id => {
              const c = byId[id];
              return (
                <div key={id} {...activate(() => onOpen && onOpen(id))}
                     style={{ padding: '6px 0', borderBottom: '1px dotted ' + C.line, cursor: onOpen ? 'pointer' : 'default' }}>
                  <span style={{ color: C.text }}>{c.id || id}</span>
                  <span>{c.claimantName ? '  -  ' + c.claimantName : ''}</span>
                  <div style={{ fontSize: 11 }}>notice served {String(acts[id].servedAt || 'date pending').slice(0, 10)}{acks[id] === undefined ? '  -  checking your acknowledgment' : ''}</div>
                </div>
              );
            })}
        {unchecked.length > 0 && (
          <div style={{ color: C.bad, marginTop: 8 }}>
            {unchecked.length} claim(s) could not be checked for a §44 notice ({unchecked.slice(0, 5).map(id => (byId[id] && byId[id].id) || id).join(', ')}
            {unchecked.length > 5 ? ', ...' : ''}). That is NOT "no notice served".
          </div>
        )}
        {filed.length > MAX && (
          <div style={{ color: C.accent, marginTop: 8 }}>
            Only the first {MAX} of {filed.length} filed claims on this desk were checked; the rest are not reflected above.
          </div>
        )}
      </div>
    </div>
  );
}
